import { CloseUniversal, TickMarkOutlined, UndoUniversal } from '@duxton-web/icons';
import {
  Alert, Button, Card, Empty, Flex, Input, Modal, Table, Tag, Tooltip, Typography,
} from '@duxton-web/ui';
import React from 'react';

import {
  formatDateTime, localeSorter, nameFromEmail,
} from './adapters';
import DiffField from './DiffField';
import {
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitCvpUpdate,
} from './permissions';
import { contentConfigModalStyle } from './theme';

const { TextArea } = Input;

const STATUS_TAG = {
  active: { color: 'success', label: 'ACTIVE' },
  pending: { color: 'processing', label: 'PENDING APPROVAL' },
  draft: { color: 'warning', label: 'LOCAL EDITS' },
};

const FieldLabel = ({ children, required }) => (
  <Typography.Body level={2} strong className="cc-field-label">
    {children}
    {required ? ' *' : ''}
  </Typography.Body>
);

const StatusTag = ({ status }) => (
  <Tag color={STATUS_TAG[status].color}>{STATUS_TAG[status].label}</Tag>
);

export const CvpList = ({
  loading,
  onOpenDetail,
  rows,
}) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'key',
      width: '22%',
      sorter: localeSorter('key'),
      render: (key) => (
        <Typography.Body level={2} className="cc-capitalize">{key}</Typography.Body>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'desc',
      render: (desc) => <Typography.Body level={2} type="secondary">{desc}</Typography.Body>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 160,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: 'Updated by',
      dataIndex: 'updatedBy',
      width: 180,
      render: (user) => <Typography.Body level={2} type="secondary">{nameFromEmail(user) || '-'}</Typography.Body>,
    },
    {
      title: 'Updated on',
      dataIndex: 'updatedAt',
      width: 200,
      render: (time) => <Typography.Body level={2} type="secondary">{formatDateTime(time)}</Typography.Body>,
    },
  ];

  return (
    <Flex vertical gap="middle">
      <Typography.Title level={4} className="cc-title-reset">
        Customer Value Propositions
      </Typography.Title>

      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        onRow={(record) => ({
          onClick: () => onOpenDetail(record.key),
          className: 'cc-row-clickable',
        })}
        locale={{ emptyText: 'No CVPs yet.' }}
      />
    </Flex>
  );
};

const CvpDiffModal = ({
  busy,
  canReview,
  canUpdate,
  isMine,
  onApprove,
  onCancel,
  onRecall,
  onReject,
  open,
  sel,
}) => {
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  return (
    <Modal
      title="Pending approval vs current"
      open={open}
      onCancel={onCancel}
      footer={sel && sel.pending ? (
        <Flex justify="flex-end" gap={8}>
          {canRecallChange ? (
            <Button size="small" loading={busy} onClick={() => { onCancel(); onRecall(); }}>Recall</Button>
          ) : null}
          {canReviewChange ? (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={() => { onCancel(); onReject(); }}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={() => { onCancel(); onApprove(); }}>Approve</Button>
            </React.Fragment>
          ) : null}
        </Flex>
      ) : null}
      width={1472}
      style={contentConfigModalStyle}
      className="cc-modal"
    >
      {sel && sel.active && sel.pending ? (
        <div className="cc-diff">
          <DiffField
            label="Description"
            activeVal={sel.active.desc}
            pendingVal={sel.pending.desc}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export const CvpDetail = ({
  base,
  busy,
  canReview,
  canUpdate,
  description,
  discardLocalEdits,
  hasEdits,
  hasPending,
  isMine,
  onApprove,
  onDescriptionChange,
  onRecall,
  onReject,
  onSendForApproval,
  pendingAuthor,
  pendingExpiry,
  sel,
  setShowDiff,
  setShowPending,
  showDiff,
  showPending,
}) => {
  if (!sel || !base) {
    return <Empty description="Select a CVP to start editing." />;
  }

  let chip = { tone: 'active', label: 'ACTIVE' };
  if (hasPending) chip = { tone: 'pending', label: 'PENDING APPROVAL' };
  else if (hasEdits) chip = { tone: 'draft', label: 'LOCAL EDITS' };

  let sendTip = '';
  if (!canUpdate) sendTip = "You don't have permission to update CVPs.";
  else if (hasPending) sendTip = 'A pending change already exists - resolve it first.';
  else if (!description.trim()) sendTip = 'Enter a description before sending for approval.';
  else if (!sel.active) sendTip = 'This CVP has no active version to update.';
  else if (!hasEdits) sendTip = 'Make a change before sending for approval.';

  const canSubmitForApproval = canSubmitCvpUpdate(
    { canUpdate },
    {
      hasActive: Boolean(sel.active),
      hasDescription: Boolean(description.trim()),
      hasEdits,
      hasPending,
    },
  );
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  return (
    <div>
      <Flex vertical gap={4} className="cc-detail-header">
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Title level={4} className="cc-title-reset cc-capitalize">{base.key}</Typography.Title>
          <Tag color={STATUS_TAG[chip.tone].color}>{chip.label}</Tag>
        </Flex>
        <Typography.Metadata type="secondary">
          Updated by {nameFromEmail(base.updatedBy || base.createdBy) || '-'}
        </Typography.Metadata>
      </Flex>

      <Flex align="center" justify="space-between" wrap="wrap" gap={12} className="cc-action-bar">
        <div />
        <Flex gap={8} align="center" wrap="wrap">
          {sel.active && sel.pending && (
            <Button size="small" onClick={() => setShowDiff(true)}>Show diff</Button>
          )}
          {hasEdits && !hasPending && (
            <Button size="small" icon={<UndoUniversal />} danger onClick={discardLocalEdits}>Discard local edits</Button>
          )}
          {hasPending && canRecallChange && (
            <Button size="small" loading={busy} onClick={onRecall}>Recall</Button>
          )}
          {hasPending && canReviewChange && (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={onReject}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={onApprove}>Approve</Button>
            </React.Fragment>
          )}
          <Tooltip title={sendTip}>
            <Button
              size="small"
              type="primary"
              icon={<TickMarkOutlined />}
              loading={busy}
              disabled={!canSubmitForApproval}
              onClick={onSendForApproval}
            >
              Send for approval
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {hasPending && sel.active && (
        <Alert
          type="info"
          showIcon
          className="cc-action-bar"
          message={(
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div className="cc-alert-content">
                <div>
                  Showing <strong>{showPending ? 'PENDING APPROVAL' : 'ACTIVE'}</strong> version.
                  {' '}Config has new changes pending approval and cannot be changed at the moment.
                </div>
                <div>
                  Changes were made by <strong>{pendingAuthor || '-'}</strong>
                  {pendingExpiry ? (
                    <React.Fragment>
                      {' '}and will expire at <strong>{pendingExpiry}</strong>
                    </React.Fragment>
                  ) : null}.
                </div>
              </div>
              <Button size="small" onClick={() => setShowPending((value) => !value)}>
                {showPending ? 'See active' : 'See changes'}
              </Button>
            </Flex>
          )}
        />
      )}

      <Card variant="borderless">
        <FieldLabel required>Description</FieldLabel>
        <TextArea
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          rows={6}
          disabled={hasPending || !canUpdate}
        />
      </Card>

      <CvpDiffModal
        busy={busy}
        canReview={canReview}
        canUpdate={canUpdate}
        isMine={isMine}
        onApprove={onApprove}
        onCancel={() => setShowDiff(false)}
        onRecall={onRecall}
        onReject={onReject}
        open={showDiff}
        sel={sel}
      />
    </div>
  );
};
